import {
  SACRAMENTO_LABELS,
  SACRAMENTOS,
  type AlunoDto,
  type CreateAlunoInput,
  type Sacramento,
  type TurmaDto,
} from '@sheepcount/shared';
import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChipToggle } from '@/components/ui/chip-toggle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { PageLoading } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { TurmaFilterSelect, turmaFiltroValido, useTurmaFilter } from '@/lib/turma-filter';

interface AlunoDraft {
  nome: string;
  idade: string;
  telefone: string;
  turmaId: string;
  sacramentos: Sacramento[];
}

function emptyDraft(turmas: TurmaDto[], turmaPreferida: string): AlunoDraft {
  return {
    nome: '',
    idade: '',
    telefone: '',
    turmaId: turmaPreferida !== '' ? turmaPreferida : (turmas[0]?.id ?? ''),
    sacramentos: [],
  };
}

export function AlunosPage() {
  const toast = useToast();
  const { turmaId: turmaFiltro } = useTurmaFilter();
  const [alunos, setAlunos] = useState<AlunoDto[] | null>(null);
  const [turmas, setTurmas] = useState<TurmaDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AlunoDraft>({
    nome: '',
    idade: '',
    telefone: '',
    turmaId: '',
    sacramentos: [],
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [alunosData, turmasData] = await Promise.all([
        api<AlunoDto[]>('/alunos'),
        api<TurmaDto[]>('/turmas'),
      ]);
      setAlunos(alunosData);
      setTurmas(turmasData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar alunos');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openNew() {
    setEditingId(null);
    // Com um filtro de turma ativo, o novo aluno já nasce nessa turma.
    setDraft(emptyDraft(turmas, turmaFiltroValido(turmaFiltro, turmas)));
    setSheetOpen(true);
  }

  function openEdit(aluno: AlunoDto) {
    setEditingId(aluno.id);
    setDraft({
      nome: aluno.nome,
      idade: aluno.idade != null ? String(aluno.idade) : '',
      telefone: aluno.telefone ?? '',
      turmaId: aluno.turmaId,
      sacramentos: [...aluno.sacramentos],
    });
    setSheetOpen(true);
  }

  function toggleSacramento(sac: Sacramento) {
    setDraft((d) => ({
      ...d,
      sacramentos: d.sacramentos.includes(sac)
        ? d.sacramentos.filter((s) => s !== sac)
        : [...d.sacramentos, sac],
    }));
  }

  async function handleSave() {
    if (!draft.nome.trim()) {
      toast('Informe o nome do aluno');
      return;
    }
    if (!draft.turmaId) {
      toast('Escolha a turma do aluno');
      return;
    }
    const payload: CreateAlunoInput = {
      nome: draft.nome.trim(),
      idade: draft.idade.trim() ? Number(draft.idade) : null,
      telefone: draft.telefone.trim() ? draft.telefone.trim() : null,
      turmaId: draft.turmaId,
      sacramentos: draft.sacramentos,
    };
    setSaving(true);
    try {
      if (editingId) {
        await api(`/alunos/${editingId}`, { method: 'PUT', body: payload });
      } else {
        await api('/alunos', { method: 'POST', body: payload });
      }
      setSheetOpen(false);
      toast(editingId ? 'Aluno atualizado' : 'Aluno criado');
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao salvar aluno');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!editingId) return;
    if (!window.confirm('Excluir este aluno? Esta ação não pode ser desfeita.')) return;
    setSaving(true);
    try {
      await api(`/alunos/${editingId}`, { method: 'DELETE' });
      setSheetOpen(false);
      toast('Aluno excluído');
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao excluir aluno');
    } finally {
      setSaving(false);
    }
  }

  const filtroAtivo = turmaFiltroValido(turmaFiltro, turmas);
  const alunosVisiveis =
    alunos === null || filtroAtivo === ''
      ? alunos
      : alunos.filter((aluno) => aluno.turmaId === filtroAtivo);

  return (
    <div>
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[22px] font-bold tracking-[-0.01em]">Alunos</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <TurmaFilterSelect turmas={turmas} />
          <Button size="sm" className="px-4 py-[9px]" onClick={openNew}>
            + Novo aluno
          </Button>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3">
          <div className="text-[13px] text-danger">{error}</div>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Tentar novamente
          </Button>
        </div>
      ) : alunosVisiveis === null ? (
        <PageLoading />
      ) : alunosVisiveis.length === 0 ? (
        <div className="text-[13px] text-muted">
          {filtroAtivo !== ''
            ? 'Nenhum aluno nesta turma ainda.'
            : 'Nenhum aluno cadastrado ainda.'}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-3.5">
          {alunosVisiveis.map((aluno) => (
            <Card
              key={aluno.id}
              role="button"
              tabIndex={0}
              onClick={() => openEdit(aluno)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openEdit(aluno);
              }}
              className="flex cursor-pointer flex-col gap-2 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 break-words text-[15px] font-bold">{aluno.nome}</div>
                {aluno.idade != null && (
                  <div className="shrink-0 text-xs text-muted">{aluno.idade} anos</div>
                )}
              </div>
              <div className="text-[12.5px] font-semibold text-primary-dark">
                {aluno.turmaNome}
              </div>
              {aluno.telefone && <div className="text-[12.5px] text-muted">{aluno.telefone}</div>}
              {aluno.sacramentos.length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-1.5">
                  {aluno.sacramentos.map((sac) => (
                    <Badge key={sac} variant="primary">
                      {SACRAMENTO_LABELS[sac]}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetTitle>{editingId ? 'Editar aluno' : 'Novo aluno'}</SheetTitle>
          <div className="flex flex-col gap-3.5">
            <div>
              <Label htmlFor="aluno-nome">Nome completo</Label>
              <Input
                id="aluno-nome"
                className="mt-1.5"
                placeholder="Nome do aluno"
                value={draft.nome}
                onChange={(e) => setDraft((d) => ({ ...d, nome: e.target.value }))}
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="aluno-idade">Idade</Label>
                <Input
                  id="aluno-idade"
                  className="mt-1.5"
                  type="number"
                  min={1}
                  max={120}
                  placeholder="9"
                  value={draft.idade}
                  onChange={(e) => setDraft((d) => ({ ...d, idade: e.target.value }))}
                />
              </div>
              <div className="flex-[1.4]">
                <Label htmlFor="aluno-telefone">Telefone</Label>
                <Input
                  id="aluno-telefone"
                  className="mt-1.5"
                  placeholder="(11) 90000-0000"
                  value={draft.telefone}
                  onChange={(e) => setDraft((d) => ({ ...d, telefone: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="aluno-turma">Turma</Label>
              <Select
                id="aluno-turma"
                className="mt-1.5"
                value={draft.turmaId}
                onChange={(e) => setDraft((d) => ({ ...d, turmaId: e.target.value }))}
              >
                <option value="" disabled>
                  Escolha a turma
                </option>
                {turmas.map((turma) => (
                  <option key={turma.id} value={turma.id}>
                    {turma.nome}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Sacramentos a receber</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {SACRAMENTOS.map((sac) => (
                  <ChipToggle
                    key={sac}
                    selected={draft.sacramentos.includes(sac)}
                    onClick={() => toggleSacramento(sac)}
                  >
                    {SACRAMENTO_LABELS[sac]}
                  </ChipToggle>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-[22px] flex gap-2.5">
            {editingId && (
              <Button
                variant="destructive-outline"
                disabled={saving}
                onClick={() => void handleDelete()}
              >
                Excluir
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="secondary" disabled={saving} onClick={() => setSheetOpen(false)}>
              Cancelar
            </Button>
            <Button className="font-bold" disabled={saving} onClick={() => void handleSave()}>
              Salvar
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
